/**
 * Centralized signup URL builder for the marketing site.
 * All CTAs that link to signup should use getSignupUrl()
 * so promo codes and params are applied consistently.
 *
 * To disable the promo, set ACTIVE_PROMO to null.
 */

import { APP_BASE } from '@/lib/constants';
import type { NavLink, SignupIntent, SignupRole } from '@/lib/types';

export const SIGNUP_BASE_URL = `${APP_BASE}/signup`;

/** Current active promo code. Set to null to disable. */
export const ACTIVE_PROMO: string | null = 'launch50';

interface SignupUrlOptions {
  role?: SignupRole;
  email?: string;
  promo?: string | null;
  provider?: string;
}

/**
 * Build a signup URL with optional role, email, promo, and provider params.
 * Automatically includes ACTIVE_PROMO unless promo is explicitly set to null.
 */
export function getSignupUrl(options: SignupUrlOptions = {}): string {
  const params = new URLSearchParams();

  if (options.role) {
    params.set('role', options.role);
  }
  if (options.email) {
    params.set('email', options.email);
  }
  if (options.provider) {
    params.set('provider', options.provider);
  }

  // Use explicit promo if provided, otherwise fall back to ACTIVE_PROMO
  const promo = options.promo !== undefined ? options.promo : ACTIVE_PROMO;
  if (promo) {
    params.set('promo', promo);
  }

  const qs = params.toString();
  return qs ? `${SIGNUP_BASE_URL}?${qs}` : SIGNUP_BASE_URL;
}

/** Signup URL for a registration intent ('choose' = no preselected role). */
export function getSignupHref(intent: SignupIntent): string {
  return getSignupUrl(intent === 'choose' ? {} : { role: intent });
}

/**
 * Registration intent a link opens in the signup dialog, or null when it simply
 * navigates. As on canideal.org, a signup link that has a page route opens the
 * dialog only where the caller asks for it (`dialog`: the homepage masthead,
 * phone menu and footer); everywhere else it goes to that page. A signup link
 * without a page (Register) always opens the dialog.
 */
export function signupDialogIntent(link: NavLink, dialog = false): SignupIntent | null {
  if (!('signup' in link)) return null;
  return dialog || !link.href ? link.signup : null;
}

/** Resolve a nav/footer link to its href (signup links without a page go through the builder). */
export function resolveNavHref(link: NavLink): string {
  if ('signup' in link) return link.href ?? getSignupHref(link.signup);
  return link.href;
}
